import { buildQueryParams, cleanParams } from "@/utils";

const NOTICES_CHANGED_EVENT = "notices:changed";
const NOTICES_CHANGED_STORAGE_KEY = "notices:changed:ts";

const emitNoticesChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTICES_CHANGED_EVENT));
    try {
      window.localStorage.setItem(
        NOTICES_CHANGED_STORAGE_KEY,
        String(Date.now())
      );
    } catch (error) {
      // Ignore storage errors (private mode/quota), same-tab event still works.
    }
  }
};

export const noticesChangedEventName = NOTICES_CHANGED_EVENT;
export const noticesChangedStorageKey = NOTICES_CHANGED_STORAGE_KEY;

/**
 * Получает список уведомлений.
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные уведомлений.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getList = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/notifications?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает точное количество непрочитанных уведомлений для текущего пользователя.
 * @returns {Promise<number>} - Количество непрочитанных уведомлений.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getUnreadCount = async () => {
  try {
    const response = await fetch("/api/notifications/unread-count", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return Number(data.unread_count) || 0;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список разделов уведомлений.
 * @returns {Promise<Object>} - Данные разделов уведомлений.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getNotificationTypes = async () => {
  try {
    const response = await fetch(`/api/notification-types?`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.types;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список событий уведомлений.
 * @returns {Promise<Object>} - Данные событий уведомлений.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getNotificationRules = async () => {
  try {
    const response = await fetch(`/api/notification-rules?`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.rules;
  } catch (err) {
    throw err;
  }
};

/**
 * Отмечает уведомления как прочитанные.
 * @param {Array} uuids - Массив уведомлений. Ничего или пустой массив, если нужно прочитать все уведомления.
 * @returns {Promise<Object>} - Данные уведомлений.
 * @throws {Error} - Ошибка при отметке уведомлений как прочитанных.
 */
export const readNotices = async (uuids) => {
  try {
    const response = await fetch("/api/notification/read", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uuids }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    emitNoticesChanged();
    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Отмечает уведомления обратно как непрочитанные.
 * @param {Array} uuids - Массив уведомлений.
 * @returns {Promise<Object>} - Данные уведомлений.
 * @throws {Error} - Ошибка при отметке уведомлений как непрочитанных.
 */
export const unreadNotices = async (uuids) => {
  try {
    const response = await fetch("/api/notification/unread", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uuids }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    emitNoticesChanged();
    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаляет уведомления.
 * @param {Array} uuids - Массив уведомлений.
 * @returns {Promise<Object>} - Данные уведомлений.
 * @throws {Error} - Ошибка при удалении уведомлений.
 */
export const deleteNotices = async (uuids) => {
  try {
    const response = await fetch("/api/notification/delete", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uuids }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    emitNoticesChanged();
    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает настройки уведомлений.
 * @returns {Promise<Object>} - Данные настроек уведомлений.
 * @throws {Error} - Ошибка при получении настроек уведомлений.
 */
export const getNotificationSettings = async () => {
  try {
    const response = await fetch("/api/notification-settings", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data.settings;
  } catch (err) {
    throw err;
  }
};

export const setNotificationSettings = async (settings) => {
  try {
    const response = await fetch("/api/notification-settings/set-individual", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при сохранении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает админские настройки уведомлений.
 * @returns {Promise<Object>} - Данные настроек уведомлений.
 * @throws {Error} - Ошибка при получении настроек уведомлений.
 */
export const getAdminNotificationRules = async () => {
  try {
    const response = await fetch("/api/admin/notification-rules", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data.rules;
  } catch (err) {
    throw err;
  }
};

export const setNotificationRule = async (settings) => {
  try {
    const response = await fetch(
      `/api/admin/notification-rule/${settings.rule_uuid}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при сохранении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Каталог переменных шаблонов уведомлений (notification_template_variables).
 * @returns {Promise<Array>} - массив переменных
 *   `{ uuid, unified_name, object_key, object_label, attribute_key, resolver_key, description, is_system, updated_at }`.
 * @throws {Error}
 */
export const getNotificationTemplateVariables = async () => {
  try {
    const response = await fetch(
      "/api/admin/notification-template-variables",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data.variables;
  } catch (err) {
    throw err;
  }
};

/**
 * Список шаблонов уведомлений (админ).
 * @param {{ search?: string }} [params]
 * @returns {Promise<Array>}
 */
export const listNotificationTemplates = async (params = {}) => {
  try {
    const search = new URLSearchParams();
    if (params.search) search.set("search", params.search);
    const qs = search.toString();
    const url = `/api/admin/notification-templates${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }
    return data.templates;
  } catch (err) {
    throw err;
  }
};

export const getNotificationTemplate = async (uuid) => {
  try {
    const response = await fetch(`/api/admin/notification-template/${uuid}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }
    return data.template;
  } catch (err) {
    throw err;
  }
};

export const createNotificationTemplate = async (payload) => {
  try {
    const response = await fetch("/api/admin/notification-template", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(
        data.error || "Не удалось создать шаблон. Попробуйте снова"
      );
    }
    return data.template;
  } catch (err) {
    throw err;
  }
};

export const updateNotificationTemplate = async (uuid, payload) => {
  try {
    const response = await fetch(`/api/admin/notification-template/${uuid}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(
        data.error || "Не удалось сохранить шаблон. Попробуйте снова"
      );
      err.status = response.status;
      err.body = data;
      throw err;
    }
    return data.template;
  } catch (err) {
    throw err;
  }
};

export const deleteNotificationTemplate = async (uuid, updatedAt) => {
  try {
    const qs = updatedAt
      ? `?updated_at=${encodeURIComponent(updatedAt)}`
      : "";
    const response = await fetch(
      `/api/admin/notification-template/${uuid}${qs}`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(
        data.error || "Не удалось удалить шаблон. Попробуйте снова"
      );
      err.status = response.status;
      err.body = data;
      throw err;
    }
    return data.ok;
  } catch (err) {
    throw err;
  }
};

/**
 * Фиксированная схема объект+атрибут для формы создания переменной.
 * @returns {Promise<Array>} - `[{ object_key, object_label, attributes: [{ attribute_key, unified_name, resolver_key, description }] }]`
 */
export const getNotificationVariableSchema = async () => {
  try {
    const response = await fetch("/api/admin/notification-variable-schema", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }
    return data.objects;
  } catch (err) {
    throw err;
  }
};

/**
 * Создать переменную каталога (привязка к паре объект+атрибут из схемы).
 * @param {{ object_key: string, attribute_key: string, description?: string }} payload
 */
export const createNotificationTemplateVariable = async (payload) => {
  try {
    const response = await fetch("/api/admin/notification-template-variable", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(
        data.error || "Не удалось создать переменную. Попробуйте снова"
      );
      err.status = response.status;
      err.body = data;
      throw err;
    }
    return data.variable;
  } catch (err) {
    throw err;
  }
};

export const updateNotificationTemplateVariable = async (uuid, payload) => {
  try {
    const response = await fetch(
      `/api/admin/notification-template-variable/${uuid}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(
        data.error || "Не удалось сохранить переменную. Попробуйте снова"
      );
      err.status = response.status;
      err.body = data;
      throw err;
    }
    return data.variable;
  } catch (err) {
    throw err;
  }
};

export const deleteNotificationTemplateVariable = async (uuid, updatedAt) => {
  try {
    const qs = updatedAt ? `?updated_at=${encodeURIComponent(updatedAt)}` : "";
    const response = await fetch(
      `/api/admin/notification-template-variable/${uuid}${qs}`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(
        data.error || "Не удалось удалить переменную. Попробуйте снова"
      );
      err.status = response.status;
      err.body = data;
      throw err;
    }
    return data.ok;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение подписок.
 * @param {Object} item - Данные подписок.
 * @returns {Promise<Object>} - Данные подписок.
 * @throws {Error} - Ошибка при получении подписок.
 */
export const getSubscribes = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/notification/subscriptions?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение подписки.
 * @param {Object} item - Данные подписки.
 * @returns {Promise<Object>} - Данные подписки.
 * @throws {Error} - Ошибка при получении подписки.
 */
export const getSubscribe = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/notification/subscribe?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавляет подписки.
 * @param {Object} item - Данные подписок.
 * @returns {Promise<Object>} - Данные подписок.
 * @throws {Error} - Ошибка при добавлении подписки.
 */
export const subscribe = async (item) => {
  try {
    const response = await fetch("/api/notification/subscribe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаляет подписки.
 * @param {Object} item - Данные подписок.
 * @returns {Promise<Object>} - Данные подписок.
 * @throws {Error} - Ошибка при удалении подписки.
 */
export const unsubscribe = async (item) => {
  try {
    const response = await fetch("/api/notification/unsubscribe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(`Ошибка сервера. ${data.error || "Попробуйте снова"}`);
    }

    return data;
  } catch (err) {
    throw err;
  }
};
